import { Experiment, FeatureRollout } from '@ms/yammer-data/dist/state/types';
import { useCanCurrentUserAccessAdminSettings } from '@ms/yammer-hooks/dist/adminSettings';
import { useIsAskMeAnythingEnabled, useIsCommunityAskMeAnythingEnabled } from '@ms/yammer-hooks/dist/askMeAnything';
import {
  useCanAccessCampaignsDashboard,
  useCanAccessCampaignsRecommendations,
  useIsCampaignSearchEnabled,
} from '@ms/yammer-hooks/dist/campaignRights';
import {
  useAndReportExperimentTreatment,
  useExperimentTreatmentWithoutReporting,
} from '@ms/yammer-hooks/dist/experiments';
import { useIsFeatureRolloutEnabled } from '@ms/yammer-hooks/dist/featureRollouts';
import { useIsNewGroupFilesEnabled } from '@ms/yammer-hooks/dist/groups';
import {
  useCanUserAccessLeaderDirectory,
  useCanUserAccessLeadershipCorner,
} from '@ms/yammer-hooks/dist/leadershipCorner';
import { useShouldShowNewEventsLayout } from '@ms/yammer-hooks/dist/routes';
import { useCurrentSessionUser } from '@ms/yammer-hooks/dist/session';
import { useCanCurrentUserAccessEnhancedUserProfile } from '@ms/yammer-hooks/dist/userProfile';
import {
  LegacyCreateGroup,
  LegacyGroupDelete,
  LegacyGroupFiles,
  LegacyGroupInfo,
  LegacyGroupInsights,
  LegacyGroupSettings,
  LegacyNetworkAdminSettings,
  LegacyUserSettings,
} from '@ms/yammer-web-components/dist/route/legacyRoutes';
import {
  eventPageMatchPath,
  insightsLeadersOrgPage,
  searchPathCampaigns,
  searchPathFiles,
  searchPathGlobalQuestions,
  searchPathGroups,
  searchPathInbox,
  searchPathThreads,
  searchPathTopics,
  searchPathUsers,
} from '@ms/yammer-web-support/dist/urls';
import { useIsTeamsHub } from 'hooks/selectors';
import { useIsTeamsFullWidthLayoutEnabled } from 'hooks/useIsTeamsFullWidthLayoutEnabled';
import React, { FC } from 'react';
import { Route, Switch } from 'react-router-dom';

import Page from '../Page';
import PageRoot from '../PageRoot';

import NonTeamsHubGroupFiles from './NonTeamsHubGroupFiles';
import {
  AdminSettings,
  AskMeAnything,
  AskMeAnythingCommunityEvents,
  AskMeAnythingEventCreation,
  AskMeAnythingLeadersEvents,
  AskMeAnythingUserEvents,
  Campaign,
  CampaignsDashboard,
  CampaignsHub,
  Configure,
  DelegationSettings,
  DiscoverGroups,
  Feed,
  Group,
  GroupInsights,
  Inbox,
  InsightsCampaign,
  InsightsCampaignRouter,
  InsightsLeaders,
  InsightsNetwork,
  InsightsQnA,
  LeaderDashboard,
  LeadersCuratedFeed,
  LeadersDirectory,
  LeadershipCorner,
  LegacyRouteConverter,
  LiveEvent,
  ManageAudience,
  QuestionSearch,
  QuestionThread,
  Questions,
  Search,
  StorylineAdminSettings,
  StorylineAdminSettingsNotification,
  StorylineHome,
  Thread,
  Topic,
  TopicNetworkQuestion,
  TopicNetworkQuestionBrowse,
  UsagePolicy,
  User,
  UserAchievements,
} from './loadableComponents';

const Routes: FC = () => {
  const shouldShowNewEventsLayout = useShouldShowNewEventsLayout();
  const isFullWidthLayout = useIsTeamsFullWidthLayoutEnabled();

  return (
    <Switch>
      <Route path={eventPageMatchPath}>
        <Page
          collapseLeftSidebar={shouldShowNewEventsLayout}
          noSpacing={shouldShowNewEventsLayout}
          noScroll={shouldShowNewEventsLayout}
        >
          <LiveEvent />
        </Page>
      </Route>
      <Route default={true}>
        <Page fullWidthLayout={isFullWidthLayout}>
          <ContentRoutes />
        </Page>
      </Route>
    </Switch>
  );
};

// eslint-disable-next-line complexity, max-lines-per-function
export const ContentRoutes: FC = () => {
  const isEnhancedUserProfileEnabled = useCanCurrentUserAccessEnhancedUserProfile();
  const canUserAccessLeadershipCorner = useCanUserAccessLeadershipCorner();
  const isStorylineHomeEnabled = useIsFeatureRolloutEnabled(FeatureRollout.StorylineHome);
  const isNewGroupFilesEnabled = useIsNewGroupFilesEnabled();
  const isTeamsHub = useIsTeamsHub();
  const isLeaderAudienceFeedEnabled =
    useExperimentTreatmentWithoutReporting(Experiment.LeaderAudienceFeed) === 'enabled';
  const isNetworkQuestionsEnabledInGA = useExperimentTreatmentWithoutReporting(Experiment.AnswersInVivaGA) === true;
  const isAskMeAnythingEnabled = useIsAskMeAnythingEnabled();
  const isCommunityAskMeAnythingEnabled = useIsCommunityAskMeAnythingEnabled();
  const isLeadershipCuratedFeedEnabled = useExperimentTreatmentWithoutReporting(Experiment.VivaLeadershipCuratedFeed);
  const isAnswerSearchEnabled = useExperimentTreatmentWithoutReporting(Experiment.AnswerSearch) === true;

  const isDraftPostStorylineFeatureEnabled =
    useExperimentTreatmentWithoutReporting(Experiment.DraftPostStoryline) === true;
  const userDraftsRoutes = isDraftPostStorylineFeatureEnabled ? (
    <Route exact={true} path="/users/:userId/drafts" component={User} />
  ) : null;
  const canAccessAdminPage = useCanCurrentUserAccessAdminSettings();

  const isStorylineAdminEnabled = useAndReportExperimentTreatment(Experiment.StorylineAdmin) === 'enabled';
  const { isNetworkAdmin } = useCurrentSessionUser();

  const canUserAccessLeaderDirectory = useCanUserAccessLeaderDirectory();

  const isCampaignsDashboardEnabled = useCanAccessCampaignsDashboard();
  const canAccessCampaignPage = useCanAccessCampaignsRecommendations();
  const isCampaignSearchEnabled = useIsCampaignSearchEnabled();
  const isStorylineAdminAdvancedEnabled =
    useAndReportExperimentTreatment(Experiment.StorylineAdminAdvanced) === 'enabled';
  const hasStorylineAdminRights = isStorylineAdminEnabled && isNetworkAdmin;

  const userProfileStorylinePage = isEnhancedUserProfileEnabled ? (
    <Route exact={true} path="/users/:userId/storyline" component={User} />
  ) : null;

  const userProfileLeaderAudiencePage = isLeaderAudienceFeedEnabled ? (
    <Route exact={true} path="/users/:userId/audiencefeed" component={User} />
  ) : null;

  const userProfileBookmarksPage = isEnhancedUserProfileEnabled ? (
    <Route exact={true} path="/users/:userId/bookmarks" component={User} />
  ) : null;

  const storylineHomePage = isStorylineHomeEnabled ? (
    <Route exact={true} path="/storylines" component={StorylineHome} />
  ) : null;

  const legacyGroupFilesPage = !isNewGroupFilesEnabled ? (
    <Route exact={true} path="/groups/:groupId/files" component={LegacyGroupFiles} />
  ) : null;

  const nonTeamsHubGroupFilesPage =
    isNewGroupFilesEnabled && !isTeamsHub ? (
      <Route exact={true} path="/groups/:groupId/files" component={NonTeamsHubGroupFiles} />
    ) : null;

  const leadersDirectoryPages = canUserAccessLeaderDirectory
    ? [
        <Route key="Leaders" exact={true} path="/leaders" component={LeadersDirectory} />,
        <Route key="LeadersList" exact={true} path="/leaders/:subRoute" component={LeadersDirectory} />,
      ]
    : null;

  const leadershipCorner = canUserAccessLeadershipCorner ? (
    <Route exact={true} path="/leadership-corner" component={LeadershipCorner} />
  ) : null;

  const leadersCuratedFeed =
    canUserAccessLeadershipCorner && isLeadershipCuratedFeedEnabled
      ? [
          <Route
            key="LeadersCuratedFeed"
            exact={true}
            path="/leadership-corner/featured-conversations"
            component={LeadersCuratedFeed}
          />,
        ]
      : null;

  const leaderDashboard = canUserAccessLeadershipCorner ? (
    <Route exact={true} path="/leader-dashboard" component={LeaderDashboard} />
  ) : null;

  const askMeAnythingPage = isAskMeAnythingEnabled ? (
    <Route path="/ama/:teamsMeetingId" component={AskMeAnything} />
  ) : null;

  const askMeAnythingUserEventsPage = isAskMeAnythingEnabled ? (
    <Route exact={true} path="/users/:userId/events/askmeanything" component={AskMeAnythingUserEvents} />
  ) : null;

  const askMeAnythingCommunityEventsPage = isCommunityAskMeAnythingEnabled ? (
    <Route
      key="askmeanything-community-events"
      path="/groups/:groupId/events/askmeanything"
      component={AskMeAnythingCommunityEvents}
    />
  ) : null;

  const askMeAnythingLeadersEventsPage = isAskMeAnythingEnabled ? (
    <Route exact={true} path="/leadership-corner/events/askmeanything" component={AskMeAnythingLeadersEvents} />
  ) : null;

  const storylineAdminSettingsRoutes = hasStorylineAdminRights ? (
    <Route
      key="storylineAdminSettings"
      exact={true}
      path="/admin/settings/storyline"
      component={StorylineAdminSettings}
    />
  ) : null;

  const storylineAdminAdvancedSettingsRoutes =
    hasStorylineAdminRights && isStorylineAdminAdvancedEnabled ? (
      <Route
        key="storylineAdminNotificationSettingsPage"
        exact={true}
        path="/admin/storyline/advanced"
        component={StorylineAdminSettingsNotification}
      />
    ) : null;

  const achievementsPage = isNetworkQuestionsEnabledInGA ? (
    <Route key="UserAchievements" exact={true} path="/achievements/:userId" component={UserAchievements} />
  ) : null;

  const adminPage = canAccessAdminPage ? <Route key="AdminHome" path="/admin" component={AdminSettings} /> : null;

  const campaignsDashboardPage = isCampaignsDashboardEnabled
    ? [
        <Route
          key="CampaignsDashboard"
          exact={true}
          path="/admin/campaigns-dashboard"
          component={CampaignsDashboard}
        />,
        <Route
          key="CampaignsDashboardSubRoute"
          exact={true}
          path="/admin/campaigns-dashboard/:campaignsDashboardSubRoute"
          component={CampaignsDashboard}
        />,
      ]
    : null;

  const searchCampaignRoute =
    isCampaignSearchEnabled && canAccessCampaignPage ? (
      <Route exact={true} path={searchPathCampaigns} component={Search} />
    ) : null;

  const searchAnswerRoute =
    isAnswerSearchEnabled && isNetworkQuestionsEnabledInGA ? (
      <Route exact={true} path={searchPathGlobalQuestions} component={Search} />
    ) : null;

  return (
    <Switch>
      <Route exact={true} path="/feed" component={Feed} />
      {achievementsPage}
      {storylineHomePage}
      <Route exact={true} path="/inbox" component={Inbox} />
      <Route exact={true} path="/inbox/:inboxFeedSubRoute" component={Inbox} />
      {campaignsDashboardPage}
      <Route key="CampaignsHub" exact={true} path="/campaigns-hub" component={CampaignsHub} />
      <Route exact={true} path="/campaigns/:campaignId" component={Campaign} />
      <Route exact={true} path="/create-group" component={LegacyCreateGroup} />
      <Route exact={true} path="/groups/:groupId" component={Group} />
      <Route exact={true} path="/groups/:groupId/activity/:groupActivitySubRoute" component={GroupInsights} />
      <Route exact={true} path="/groups/:groupId/settings" component={LegacyGroupSettings} />
      <Route exact={true} path="/groups/:groupId/delete" component={LegacyGroupDelete} />
      {legacyGroupFilesPage}
      {nonTeamsHubGroupFilesPage}
      <Route exact={true} path="/groups/:groupId/info" component={LegacyGroupInfo} />
      <Route exact={true} path="/groups/:groupId/insights" component={LegacyGroupInsights} />
      <Route exact={true} path="/groups/:groupId/:groupFeedSubRoute" component={Group} />
      <Route exact={true} path="/threads/:threadId/:topLevelMessageId?/:secondLevelMessageId?" component={Thread} />
      <Route exact={true} path="/users/:userId" component={User} />
      {userProfileStorylinePage}
      {userProfileLeaderAudiencePage}
      {userProfileBookmarksPage}
      {userDraftsRoutes}
      <Route exact={true} path="/topics/:topicId" component={Topic} />
      <Route exact={true} path="/usage-policy" component={UsagePolicy} />
      <Route exact={true} path="/user-settings" component={LegacyUserSettings} />
      <Route exact={true} path="/user-settings/delegates" component={DelegationSettings} />
      <Route exact={true} path="/network-admin" component={LegacyNetworkAdminSettings} />
      <Route exact={true} path="/configure" component={Configure} />
      <Route exact={true} path={searchPathThreads} component={Search} />
      <Route exact={true} path={searchPathGroups} component={Search} />
      <Route exact={true} path={searchPathUsers} component={Search} />
      <Route exact={true} path={searchPathFiles} component={Search} />
      <Route exact={true} path={searchPathInbox} component={Search} />
      <Route exact={true} path={searchPathTopics} component={Search} />
      {searchCampaignRoute}
      {searchAnswerRoute}
      <Route exact={true} path="/discover-groups" component={DiscoverGroups} />
      <Route exact={true} path="/discover-groups/:discoverGroupsSubRoute" component={DiscoverGroups} />
      <Route key="QuestionsSearch" exact={true} path="/questions/search" component={QuestionSearch} />
      <Route
        key="Question"
        exact={true}
        path="/questions/:threadId/:topLevelMessageId?/:secondLevelMessageId?"
        component={QuestionThread}
      />
      <Route key="Questions" exact={true} path="/questions" component={Questions} />
      <Route
        key="QuestionsTopicBrowse"
        exact={true}
        path="/topics-network-question/browse"
        component={TopicNetworkQuestionBrowse}
      />
      <Route
        key="TopicNetworkQuestionFeed"
        exact={true}
        path="/topics-network-question/:topicId"
        component={TopicNetworkQuestion}
      />
      <Route
        key="TopicNetworkQuestionFeedSubroute"
        exact={true}
        path="/topics-network-question/:topicId/:topicFeedSubRoute"
        component={TopicNetworkQuestion}
      />
      <Route
        key="ManageAudience"
        exact={true}
        path="/user-settings/leaders/manage/:userId"
        component={ManageAudience}
      />
      {leadersDirectoryPages}
      {leadershipCorner}
      {leaderDashboard}
      {leadersCuratedFeed}
      <Route exact={true} path="/insights/campaign" component={InsightsCampaignRouter} />
      <Route exact={true} path="/insights/campaign/:campaignId" component={InsightsCampaign} />
      <Route exact={true} path="/insights/leaders" component={InsightsLeaders} />
      <Route exact={true} path={insightsLeadersOrgPage} component={InsightsLeaders} />
      <Route exact={true} path="/insights/leaders/:leaderId" component={InsightsLeaders} />
      <Route exact={true} path="/insights/users/myInsights" component={InsightsLeaders} />
      <Route exact={true} path="/insights/users/myInsights/leaders/:leaderId" component={InsightsLeaders} />
      <Route exact={true} path="/insights/qna" component={InsightsQnA} />
      <Route exact={true} path="/insights/qna/:insightsQnASubroute" component={InsightsQnA} />
      <Route exact={true} path="/insights/network" component={InsightsNetwork} />
      <Route exact={true} path="/ama/new/:userId?" component={AskMeAnythingEventCreation} />
      {askMeAnythingPage}
      {askMeAnythingUserEventsPage}
      {askMeAnythingCommunityEventsPage}
      {askMeAnythingLeadersEventsPage}
      {storylineAdminSettingsRoutes}
      {storylineAdminAdvancedSettingsRoutes}
      {adminPage}
      <Route exact={true} path="/" component={PageRoot} />
      <Route exact={false} path="*" component={LegacyRouteConverter} />
    </Switch>
  );
};

export default Routes;
